import React, { useCallback, useEffect, useMemo, useState } from "react";
import i18n from "i18next";
import { useDispatch } from "react-redux";
import IdealStudio from "../../../core/editor/main";
import { addBox, configScore } from "../../../common/actions";
import {
  DEFAULT_INTERACTIVE_COLOR,
  INTERACTIVE_COLORS,
  INTERACTIVE_MODELS,
  fetchInteractiveCatalog,
  generateInteractive,
} from "./interactiveItemService";
import InteractiveExample from "./InteractiveExample";
import InteractiveCreditButton from "./InteractiveCreditButton";
import "./_interactiveItem.scss";

/* eslint-disable react/prop-types */

/**
 * "İnteraktif" sekmesi — Soru Üretici modalının üçüncü sekmesi.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * NEDEN MODAL DEĞİL, PANEL
 * ──────────────────────────────────────────────────────────────────────────
 * Bu akış bir süre Bileşenler → Etkileşim panelinde kendi overlay modalı
 * olarak durdu ve orada BOZUK çıktı: carousel paneli `transform` taşıyor,
 * transform'lu bir ata ise `position: fixed`'in referansını kendisi yapıyor.
 * Overlay ekranı değil o dar paneli kapladı — 124 piksellik bir şeride
 * sıkışmış, her kelimesi alt alta düşen bir modal.
 *
 * İkinci ve asıl sebep: yer yanlıştı. Soru Üretici zaten "anlatımdan AI ile
 * üret" merkezi; sahne listesi, anlatım metni ve anlatıcı ayarları orada
 * hazır duruyor. Burası o modalın içinde yaşayan bir PANEL — kendi overlay'i,
 * kendi sahne listesi, kendi anlatım kutusu yok. Host ne gösteriyorsa onu
 * kullanır.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * ÜÇ ADIM
 * ──────────────────────────────────────────────────────────────────────────
 * Katalogda 23 aktivite var. Hepsini bir ızgarada göstermek seçim felci
 * yapıyor; "tek düğme, model karar versin" ise kullanıcıyı ne geleceğini
 * bilmeden ücretli bir üretime sokuyor.
 *
 *   1. AMAÇ  — altı kutu. 23 tip bunların altında.
 *   2. ÖNERİ — model anlatımı okur, üç somut öneri çıkarır. Ucuz, senkron,
 *              ücretsiz. İsteyen "Tüm tipleri göster" ile listeye iner.
 *   3. ONAY  — üretilen aktivite OYNANABİLİR gösterilir; beğenilirse sahneye
 *              eklenir.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * PUANSIZ TİPTE EGZERSİZ KAYDI AÇILMAZ
 * ──────────────────────────────────────────────────────────────────────────
 * `exercises{}` kaydını ADD_BOX reducer'ı açar ve açma koşulu `ids.config`
 * içindeki kategorinin "evaluation" olması. Bu yüzden config SADECE backend
 * bir `exercise` döndürdüğünde — yani tip puanlıysa — geçilir.
 *
 * Çevrilen kartların ya da zaman çizelgesinin egzersiz kaydı açılsaydı,
 * ağırlığı sıfır olsa bile eğitimin "kaç soru doğru" paydasında görünür ve
 * öğrenci hiç cevaplamadığı bir şeyden eksik alırdı.
 */
export default function InteractivePane({
  /** Host'un gösterdiği sahnenin navId'si — kutu buraya düşecek. */
  navId,
  sceneTitle,
  sceneIndex,
  trainingId,
  /** Host'un anlatım kutusundaki metin (düzenlenmiş hali). */
  narrationText,
  contentLanguage,
  onServerModal,
  onInsufficientBalance,
  /** Kutu sahneye eklendikten sonra host'u kapatmak için. */
  onInserted,
}) {
  const dispatch = useDispatch();

  const [catalog, setCatalog] = useState(null);
  // İki adım kaldı: TÜM TİPLER açık gelir, seçilen üretilir, sonuç onaylanır.
  //
  // Araya bir "amaç seç → model üç öneri çıkarsın" adımı vardı; kaldırıldı.
  // Gerekçesi şuydu: 23 tipi birden göstermek seçim felci yapar. Her tipin
  // OYNANABİLİR örneği olunca o gerekçe düştü — yazar artık tarif okumuyor,
  // şeye bakıyor. Üstelik öneri adımı bir LLM çağrısıydı: bekleme, maliyet ve
  // "model benim yerime seçti" hissi, artık karşılığı olmayan bir bedel.
  const [step, setStep] = useState("browse"); // browse | result
  const [prompt, setPrompt] = useState("");
  // Aktivitenin ANA rengi. aidea'nın moru seçili gelir; on kareden biri ya da
  // serbest renk seçiciyle değiştirilir.
  const [accentColor, setAccentColor] = useState(DEFAULT_INTERACTIVE_COLOR);
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(null); // 'suggest' | 'generate'
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Önizlemesi açık olan tip. Üretimden ÖNCE bakılır, o yüzden hiçbir şeyi
  // tetiklemez ve hiçbir şeye mal olmaz.
  const [example, setExample] = useState(null);

  useEffect(() => {
    fetchInteractiveCatalog()
      .then(setCatalog)
      .catch((err) => setError(err.message));
  }, []);

  // Sahne değişince baştan başla: önceki sahnenin önerileri bu sahneye ait
  // değil, ve üretilmiş bir sonucu başka bir sahneye eklemek sessiz bir hata.
  useEffect(() => {
    setStep("browse");
    setResult(null);
    setError(null);
  }, [navId]);

  const source = (prompt.trim() ? { prompt: prompt.trim() } : { narrationText }) || {};
  const hasInput = !!(prompt.trim() || (narrationText && narrationText.trim()));
  // Renk artık her zaman dolu (aidea'nın moruyla başlıyor), yani bugün bu
  // `hasInput` ile aynı. Yine de ayrı duruyor: üretim yolunun koşulu "konu VE
  // renk" ve renk bir gün yeniden boşalabilir hâle gelirse, o kuralın tek bir
  // yerde yazılı olması gerekir.
  const canGenerate = hasInput && !!accentColor;

  // Her iki çağrının (öneri ve üretim) paylaştığı gövde.
  const baseFields = useMemo(
    () => ({
      navId,
      sceneTitle,
      sceneIndex,
      trainingId,
      contentLanguage,
      llmModel: model,
      accentColor,
      // Yazarın yazdığı konu varsa o, yoksa sahnenin anlatımı. İkisini birden
      // göndermek, yazarın cümlesini anlatımın içinde boğmak olurdu.
      ...source,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      navId,
      sceneTitle,
      sceneIndex,
      trainingId,
      contentLanguage,
      model,
      accentColor,
      prompt,
      narrationText,
    ]
  );

  const runGenerate = useCallback(
    async (typeId, outline) => {
      setBusy("generate");
      setError(null);
      try {
        const generated = await generateInteractive({ ...baseFields, typeId, outline });
        setResult(generated);
        setStep("result");
      } catch (err) {
        // Bakiye reddini genel bir hata gibi göstermek, kullanıcıya "bir şeyler
        // ters gitti" deyip neyin ters gittiğini saklamak olurdu.
        if (err?.data?.code === "INSUFFICIENT_BALANCE") {
          onInsufficientBalance?.(err.data);
        } else if (err?.data) {
          onServerModal?.(err.data);
        }
        setError(err.message);
      } finally {
        setBusy(null);
      }
    },
    [baseFields, onInsufficientBalance, onServerModal]
  );

  const addToScene = useCallback(() => {
    if (!result?.box || !result?.toolbar) return;
    const { box, toolbar, exercise } = result;
    const boxId = result.boxId || box.id;
    const structure = toolbar.structure || {};
    const plugin = IdealStudio.Plugins.get("InteractiveItem");
    // Host hangi sahneyi gösteriyorsa oraya: üretim sırasında sahne
    // değiştirilmişse (akış baştan başlar ama yine de) kutu, backend'in
    // bildiği navId'ye değil GÖSTERİLEN sahneye ait olmalı.
    const parent = navId || box.parent;

    dispatch(
      addBox(
        {
          id: boxId,
          parent,
          container: 0,
          // SADECE puanlı tipte — dosya başındaki nota bakın.
          ...(exercise && plugin ? { config: plugin.getConfig() } : {}),
        },
        true,
        true,
        "",
        toolbar.style || {},
        toolbar.state || {},
        toolbar.structure,
        {
          name: toolbar.pluginId,
          position: box.position || { x: "0%", y: "0%", type: "absolute" },
          width: structure.width ?? 88,
          height: structure.height ?? 80,
          widthUnit: "%",
          heightUnit: "%",
        }
      )
    );

    // Reducer yeni egzersizi ağırlık 0 ile açar — yani "puanlı" dediğimiz bir
    // aktivite eğitimin toplamına sıfır katkıyla girer. Backend'in verdiği
    // ağırlık hemen ardından yazılır; yazar puan panelinden değiştirebilir.
    if (exercise?.weight) {
      dispatch(configScore(boxId, "weight", exercise.weight, parent));
    }

    setResult(null);
    setStep("browse");
    onInserted?.();
  }, [result, dispatch, navId, onInserted]);

  const types = catalog?.types || [];
  const categories = catalog?.categories || [];

  const examplePopup = example ? (
    <InteractiveExample type={example} onClose={() => setExample(null)} />
  ) : null;

  if (busy === "generate") {
    return (
      <div className="ii-pane">
        {examplePopup}
        <div className="ii-pane__busy">
          <div className="ii-pane__spinner" />
          <div>{i18n.t("InteractiveItem.Generating", { defaultValue: "Aktivite yazılıyor" })}</div>
          <small>
            {i18n.t("InteractiveItem.GeneratingMeta", {
              defaultValue:
                "Birkaç dakika sürebilir — modalı kapatabilirsin, iş arka planda devam eder.",
            })}
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="ii-pane">
      {examplePopup}
      {error && <div className="ii-editor__error">{error}</div>}

      {step === "result" && result ? (
        <>
          <div className="ii-pane__section-head">
            <p className="ii-pane__label">
              {result.typeLabel}
              {result.scored
                ? ` · ${i18n.t("InteractiveItem.Scored", { defaultValue: "puanlı" })}`
                : ` · ${i18n.t("InteractiveItem.Unscored", { defaultValue: "puansız" })}`}
            </p>
          </div>
          {/* Onay ekranı aktiviteyi OYNANABİLİR gösterir: statik bir önizleme,
              "bu doğru çalışıyor mu" sorusunu cevaplamaz. */}
          <iframe
            className="ii-preview"
            src={result.htmlUrl}
            title={result.typeLabel}
            sandbox="allow-scripts"
          />
          <div className="ii-pane__foot">
            <button
              type="button"
              className="ii-btn"
              onClick={() => {
                setResult(null);
                setStep("browse");
              }}
            >
              {i18n.t("InteractiveItem.Again", { defaultValue: "Başka bir şey dene" })}
            </button>
            <button type="button" className="ii-btn ii-btn--primary" onClick={addToScene}>
              {i18n.t("InteractiveItem.Add", { defaultValue: "Sahneye ekle" })}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="ii-pane__section-head">
            <p className="ii-pane__label">
              {i18n.t("InteractiveItem.AllTypes", { defaultValue: "Tüm tipler" })}
            </p>
            <select
              className="ii-pane__model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {INTERACTIVE_MODELS.map((m) => (
                <option key={m.value} value={m.value}>
                  {i18n.t(m.labelKey, { defaultValue: m.fallback })}
                </option>
              ))}
            </select>
          </div>

          {/* Anlatım host'un kutusundan geliyor. Bu alan onu EZER — yazarın
              "bu sahnede şu konuda bir aktivite istiyorum" diyebilmesi için. */}
          <input
            type="text"
            className="ii-pane__prompt"
            value={prompt}
            placeholder={
              narrationText
                ? i18n.t("InteractiveItem.PromptOverride", {
                    defaultValue: "Anlatım yerine başka bir konu (opsiyonel)",
                  })
                : i18n.t("InteractiveItem.PromptPlaceholder", {
                    defaultValue: "Ne üzerine olsun? Örn: yangın tüpü kullanım adımları",
                  })
            }
            onChange={(e) => setPrompt(e.target.value)}
          />

          {/* ANA RENK — on hazır kare + serbest seçici. Modele tek renk olarak
              değil, paletin türetileceği baskın renk olarak gider. */}
          <div className="ii-color">
            <span className="ii-color__label">
              {i18n.t("InteractiveItem.AccentColor", { defaultValue: "Ana renk" })}
            </span>
            <div className="ii-color__swatches">
              {INTERACTIVE_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  title={c.label}
                  aria-label={c.label}
                  className={
                    accentColor.toUpperCase() === c.value.toUpperCase()
                      ? "ii-swatch ii-swatch--on"
                      : "ii-swatch"
                  }
                  style={{ background: c.value }}
                  onClick={() => setAccentColor(c.value)}
                />
              ))}
              {/* Listede olmayan bir renk isteyen için. Seçici her zaman bir
                  değer taşıdığından, o anki renkle açılır. */}
              <label
                className="ii-swatch ii-swatch--custom"
                title={i18n.t("InteractiveItem.CustomColor", { defaultValue: "Özel renk" })}
              >
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                />
              </label>
            </div>
            <span className="ii-color__note">
              {i18n.t("InteractiveItem.AccentColorNote", {
                defaultValue: "Yüzeyler, tonlar ve degradeler bundan türetilir",
              })}
            </span>
          </div>

          {!canGenerate && (
            <p className="ii-pane__hint">
              {i18n.t("InteractiveItem.NeedInput", {
                defaultValue: "Bu sahnenin anlatımı yok — yukarıya bir konu yaz.",
              })}
            </p>
          )}

          {/* Kredi tahmini — infografik sahne modalındaki düğmenin aynısı,
              aynı yerde (sol alt). Tıklayınca sorar; model değişirse rakam
              atılır, çünkü fiyat modele bağlı. */}
          <div className="ii-pane__credit">
            <InteractiveCreditButton llmModel={model} disabled={!canGenerate} />
          </div>

          {/* TÜM TİPLER açık. Her kartta örneğini açan bir düğme var; yazar
              tarif okumak yerine şeye bakıp seçiyor. */}
          <div className="ii-browse">
            {categories.map((c) => (
              <section key={c.id}>
                <h4>{c.label}</h4>
                <div className="ii-browse__grid">
                  {types
                    .filter((t) => t.category === c.id)
                    .map((t) => (
                      <div className="ii-type-wrap" key={t.id}>
                        <button
                          type="button"
                          className="ii-type"
                          disabled={!canGenerate}
                          onClick={() => runGenerate(t.id, t.sizeHint)}
                        >
                          <strong>{t.label}</strong>
                          <span>{t.blurb}</span>
                          <em>
                            {t.sizeHint}
                            {t.scored
                              ? ` · ${i18n.t("InteractiveItem.Scored", { defaultValue: "puanlı" })}`
                              : ` · ${i18n.t("InteractiveItem.Unscored", { defaultValue: "puansız" })}`}
                          </em>
                        </button>
                        <ExampleButton type={t} onOpen={setExample} floating />
                      </div>
                    ))}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Bilgi düğmesi — tipin örneğini açar.
 *
 * Yalnızca o tipin örneği YÜKLENMİŞSE çıkar (`type.example`, katalogdan
 * gelir). Örnekler ortam başına ayrı konduğu için bir ortamda eksik olabilir;
 * düğmenin hiç çıkmaması, tıklanınca 404 veren bir düğmeden iyidir.
 */
function ExampleButton({ type, onOpen, floating }) {
  if (!type?.example) return null;
  return (
    <button
      type="button"
      className={floating ? "ii-example-btn ii-example-btn--float" : "ii-example-btn"}
      title={i18n.t("InteractiveItem.SeeExample", { defaultValue: "Örneğini gör" })}
      aria-label={i18n.t("InteractiveItem.SeeExample", { defaultValue: "Örneğini gör" })}
      onClick={(e) => {
        // Kartın kendisi üretimi başlatıyor — bu düğme ona ulaşmamalı.
        e.stopPropagation();
        e.preventDefault();
        onOpen(type);
      }}
    >
      !
    </button>
  );
}
